/**
 * Hooks for AI features (STORY-034 to STORY-038)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useCallback } from 'react'
import { aiService, getQuestionnaireTemplate, getProjectTypes } from '@/services/ai'
import type {
  InterventionRequest,
  ProjectType,
  ConversationChannel,
  ConversationStatus,
  QuestionnaireState,
} from '@/types/ai'

/**
 * Hook for managing AI conversations
 */
export function useAIConversations(filters?: {
  status?: ConversationStatus
  channel?: ConversationChannel
  search?: string
}) {
  return useQuery({
    queryKey: ['ai-conversations', filters],
    queryFn: () => aiService.getConversations(filters),
    staleTime: 30000, // 30 seconds
  })
}

/**
 * Hook for a single AI conversation
 */
export function useAIConversation(id: string | undefined) {
  return useQuery({
    queryKey: ['ai-conversation', id],
    queryFn: () => (id ? aiService.getConversation(id) : null),
    enabled: !!id,
    staleTime: 10000, // 10 seconds
  })
}

/**
 * Hook for creating a new conversation
 */
export function useCreateConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: {
      clientEmail: string
      clientName?: string
      clientId?: string
      channel: ConversationChannel
      projectType?: ProjectType
    }) => aiService.createConversation(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] })
    },
  })
}

/**
 * Hook for sending a message in a conversation
 */
export function useSendMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      conversationId,
      message,
    }: {
      conversationId: string
      message: string
    }) => aiService.processMessage(conversationId, message),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ai-conversation', variables.conversationId] })
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] })
      queryClient.invalidateQueries({ queryKey: ['ai-interventions'] })
    },
  })
}

/**
 * Hook for toggling AI on a conversation
 */
export function useToggleAI() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      conversationId,
      enabled,
    }: {
      conversationId: string
      enabled: boolean
    }) => aiService.toggleAI(conversationId, enabled),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ai-conversation', variables.conversationId] })
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] })
    },
  })
}

/**
 * Hook for generating AI summary
 */
export function useGenerateSummary() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (conversationId: string) => aiService.generateSummary(conversationId),
    onSuccess: (_, conversationId) => {
      queryClient.invalidateQueries({ queryKey: ['ai-conversation', conversationId] })
    },
  })
}

/**
 * Hook for managing intervention requests
 */
export function useInterventionRequests(filters?: {
  status?: InterventionRequest['status']
  priority?: InterventionRequest['priority']
}) {
  return useQuery({
    queryKey: ['ai-interventions', filters],
    queryFn: () => aiService.getInterventionRequests(filters),
    staleTime: 15000, // 15 seconds - refresh more often for intervention queue
  })
}

/**
 * Hook for updating intervention request
 */
export function useUpdateIntervention() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string
      updates: Partial<Pick<InterventionRequest, 'status' | 'assignedAdminId' | 'adminNotes'>>
    }) => aiService.updateInterventionRequest(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-interventions'] })
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] })
    },
  })
}

/**
 * Hook for resolving an intervention
 */
export function useResolveIntervention() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      interventionId,
      adminId,
      response,
      notes,
    }: {
      interventionId: string
      adminId: string
      response: string
      notes?: string
    }) => aiService.resolveIntervention(interventionId, adminId, response, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-interventions'] })
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] })
    },
  })
}

/**
 * Hook for BMAD questionnaire state management
 */
export function useQuestionnaire(initialProjectType?: ProjectType) {
  const [state, setState] = useState<QuestionnaireState>({
    currentQuestion: 0,
    answers: {},
    isComplete: false,
    projectType: initialProjectType || null,
  })

  const template = state.projectType ? getQuestionnaireTemplate(state.projectType) : null
  const currentQuestionConfig = template?.questions[state.currentQuestion]
  const totalQuestions = template?.questions.length || 0
  const progress = totalQuestions > 0 ? ((state.currentQuestion + 1) / totalQuestions) * 100 : 0

  const setProjectType = useCallback((type: ProjectType) => {
    setState({
      currentQuestion: 0,
      answers: {},
      isComplete: false,
      projectType: type,
    })
  }, [])

  const answerQuestion = useCallback(
    (questionId: string, answer: string | string[]) => {
      setState((prev) => ({
        ...prev,
        answers: {
          ...prev.answers,
          [questionId]: answer,
        },
      }))
    },
    []
  )

  const nextQuestion = useCallback(() => {
    setState((prev) => {
      const nextIndex = prev.currentQuestion + 1
      if (nextIndex >= totalQuestions) {
        return { ...prev, isComplete: true }
      }
      return { ...prev, currentQuestion: nextIndex }
    })
  }, [totalQuestions])

  const previousQuestion = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentQuestion: Math.max(0, prev.currentQuestion - 1),
    }))
  }, [])

  const reset = useCallback(() => {
    setState({
      currentQuestion: 0,
      answers: {},
      isComplete: false,
      projectType: null,
    })
  }, [])

  const canProceed = useCallback(() => {
    if (!currentQuestionConfig) return false
    if (!currentQuestionConfig.required) return true

    const answer = state.answers[currentQuestionConfig.id]
    if (!answer) return false
    if (Array.isArray(answer)) return answer.length > 0
    return answer.trim().length > 0
  }, [currentQuestionConfig, state.answers])

  return {
    state,
    template,
    currentQuestionConfig,
    totalQuestions,
    progress,
    projectTypes: getProjectTypes(),
    setProjectType,
    answerQuestion,
    nextQuestion,
    previousQuestion,
    reset,
    canProceed,
  }
}

/**
 * Combined hook for AI page state
 */
export function useAI() {
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null)
  const [filters, setFilters] = useState<{
    status?: ConversationStatus
    channel?: ConversationChannel
    search?: string
  }>({})

  const conversations = useAIConversations(filters)
  const selectedConversation = useAIConversation(selectedConversationId || undefined)
  const interventions = useInterventionRequests({ status: 'pending' })

  const sendMessage = useSendMessage()
  const toggleAI = useToggleAI()
  const generateSummary = useGenerateSummary()
  const resolveIntervention = useResolveIntervention()

  return {
    // State
    selectedConversationId,
    setSelectedConversationId,
    filters,
    setFilters,

    // Queries
    conversations,
    selectedConversation,
    interventions,

    // Mutations
    sendMessage,
    toggleAI,
    generateSummary,
    resolveIntervention,

    // Computed
    pendingInterventionsCount: interventions.data?.filter((i) => i.status === 'pending').length || 0,
    activeConversationsCount:
      conversations.data?.filter((c) => c.status === 'active').length || 0,
  }
}

export default useAI
